import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { createInvite, getInvites, revokeInvite } from '../api/invites';
import { getUsers } from '../api/users';
import { AdminUser, InviteRecord } from '../types/models';

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const statusForInvite = (invite: InviteRecord): 'accepted' | 'revoked' | 'expired' | 'pending' => {
  if (invite.accepted_at) return 'accepted';
  if (invite.revoked_at) return 'revoked';
  if (new Date(invite.expires_at).getTime() <= Date.now()) return 'expired';
  return 'pending';
};

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [invites, setInvites] = useState<InviteRecord[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [createdInviteUrl, setCreatedInviteUrl] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadInvites = useCallback(async () => {
    setLoadingInvites(true);
    try {
      const data = await getInvites();
      setInvites(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load invites');
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadInvites();
  }, [loadUsers, loadInvites]);

  const pendingInvites = useMemo(
    () => invites.filter((invite) => statusForInvite(invite) === 'pending'),
    [invites]
  );

  const handleCreateInvite = async () => {
    setError(null);
    setCopySuccess(false);
    if (!inviteEmail.trim()) return;
    setInviteSubmitting(true);
    try {
      const created = await createInvite(inviteEmail.trim());
      setCreatedInviteUrl(created.invite_url);
      setInviteEmail('');
      await loadInvites();
    } catch (err: any) {
      setError(err?.message || 'Failed to create invite');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!createdInviteUrl) return;
    await navigator.clipboard.writeText(createdInviteUrl);
    setCopySuccess(true);
  };

  const handleRevoke = async (id: number) => {
    setError(null);
    try {
      await revokeInvite(id);
      await loadInvites();
    } catch (err: any) {
      setError(err?.message || 'Failed to revoke invite');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Users
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Admin role is never editable from UI. Promote admins only from Rails console.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          User Directory
        </Typography>
        {loadingUsers ? (
          <CircularProgress size={24} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.admin ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{formatDate(user.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Invites</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Email-only invites. All accepted users are created as non-admin.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <TextField
            label="Invite email"
            type="email"
            size="small"
            value={inviteEmail}
            onChange={(event) => setInviteEmail(event.target.value)}
            sx={{ maxWidth: 420, width: '100%' }}
          />
          <Button variant="contained" onClick={handleCreateInvite} disabled={inviteSubmitting || !inviteEmail.trim()}>
            {inviteSubmitting ? 'Creating...' : 'Create Invite'}
          </Button>
        </Stack>

        {createdInviteUrl && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="success" sx={{ mb: 1 }}>
              Invite generated. Share this one-time link securely.
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
              <TextField fullWidth size="small" value={createdInviteUrl} InputProps={{ readOnly: true }} />
              <Button variant="outlined" onClick={handleCopyInviteLink} startIcon={<ContentCopyIcon />}>
                {copySuccess ? 'Copied' : 'Copy Link'}
              </Button>
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Pending Invites ({pendingInvites.length})
        </Typography>
        {loadingInvites ? (
          <CircularProgress size={24} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invites.map((invite) => {
                  const status = statusForInvite(invite);
                  return (
                    <TableRow key={invite.id}>
                      <TableCell>{invite.email}</TableCell>
                      <TableCell>{status}</TableCell>
                      <TableCell>{formatDate(invite.expires_at)}</TableCell>
                      <TableCell>{formatDate(invite.created_at)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="warning"
                          onClick={() => handleRevoke(invite.id)}
                          disabled={status !== 'pending'}
                        >
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default AdminUsersPage;
